import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { ApiService } from '@dav-core/services/api.service';
import { FilterService } from '@dav-core/services/filter.service';
import { FormService } from '@dav-core/services/form.service';
import { ProductService } from '@dav-core/services/product.service';
import { ContractType } from '@dav-model/api/response/pending-contract.response.model';
import { CartItem } from '@dav-model/cart/cart-item.model';
import { CartTax } from '@dav-model/cart/cart-tax.model';
import { Cart } from '@dav-model/cart/cart.model';
import { LoadQuote } from '@dav-model/cart/load-quote.model';
import { SaveQuote } from '@dav-model/cart/save-quote.model';
import { ValidatedCart } from '@dav-model/cart/validate-cart.model';
import { SubmitCartResponse } from '@dav-model/checkout/submit_cart/response/submit-cart.response.model';
import { Markup, UserResponse } from '@dav-model/login/response/user.response.model';
import { MutedProductCodes } from '@dav-model/product-codes';
import { GetProductsRequest } from '@dav-model/request/product.request';
import { GetProductsResponse } from '@dav-model/response/getproducts.response';
import { VehicleValues } from '@dav-store/vehicle/vehicle.reducer';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { environment } from 'environments/environment';
import { Observable, of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';

import * as CartActions from './cart.actions';
import * as CartReducer from '../cart/cart.reducer';
import * as ProductActions from '../product/product.actions';
import * as ProductReducer from '../product/product.reducer';
import * as fromRoot from '../reducers';
import { UpdatePendingNumberAction } from '../user/user.actions';
import * as VehicleActions from '../vehicle/vehicle.actions';

@Injectable()
export class CartEffects {
  constructor(
    private actions$: Actions,
    private apiService: ApiService,
    private router: Router,
    private store: Store<fromRoot.State>,
    private formService: FormService,
    private filterService: FilterService
  ) {}

  addCart$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(CartActions.ADD_CART),
      withLatestFrom(
        this.store.select((state) => state.productState.request),
        this.store.select((state) => state.cartState),
        this.store.select((state) => state.userState.user)
      ),
      mergeMap(
        ([action, productRequest, cartState, user]: [
          CartActions.AddCartAction,
          GetProductsRequest,
          CartReducer.State,
          UserResponse
        ]) => {
          const cart = [...cartState.validatedCart, JSON.parse(JSON.stringify(action.cartItem))];
          const request = new Cart(cart, productRequest, user);

          return this.apiService.validateCart(request).pipe(
            // If successful, dispatch success action with result
            map((response: ValidatedCart) => {
              return {
                type: CartActions.VALIDATE_CART_SUCCESS,
                payload: { validatedCart: response },
              };
            }),
            // If request fails, dispatch failed action
            catchError((error) =>
              of({
                type: CartActions.CART_FAILURE,
                payload: error,
              })
            )
          );
        }
      )
    )
  );

  deleteCart$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(CartActions.DELETE_CART),
      withLatestFrom(
        this.store.select((state) => state.productState.request),
        this.store.select((state) => state.cartState),
        this.store.select((state) => state.userState.user)
      ),
      mergeMap(
        ([action, productRequest, cartState, user]: [
          CartActions.DeleteCartAction,
          GetProductsRequest,
          CartReducer.State,
          UserResponse
        ]) => {
          const cart = cartState.validatedCart.filter((item) => item.key !== action.cartItemSelector.key);
          const request = new Cart(cart, productRequest, user);

          return this.apiService.validateCart(request).pipe(
            // If successful, dispatch success action with result
            map((response) => {
              return {
                type: CartActions.VALIDATE_CART_SUCCESS,
                payload: { validatedCart: response },
              };
            }),
            // If request fails, dispatch failed action
            catchError((error) =>
              of({
                type: CartActions.CART_FAILURE,
                payload: error,
              })
            )
          );
        }
      )
    )
  );

  getCheckoutForm$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(CartActions.GET_CHECKOUT_FORM),
      withLatestFrom(
        this.store.select((state) => state.productState.request),
        this.store.select((state) => state.cartState.cart),
        this.store.select((state) => state.userState.user)
      ),
      switchMap(
        ([, request, cart, user]: [CartActions.GetCheckouFormAction, GetProductsRequest, CartItem[], UserResponse]) => {
          const req = new Cart(cart, request, user);
          const r = {
            ...req,
            application: environment.application,
            programId: user.programId,
          };

          return this.apiService.getCheckoutForm(r).pipe(
            // If successful, dispatch success action with result

            map((response) => {
              response.creditInsuranceForm = [
                {
                  section: 'creditInsuranceForm',
                  id: '73E2BFAD-8E8F-4198-A015-7A13E236D0AC',
                  name: 'addInsurance',
                  label: 'Do you want to add Credit Insurance?',
                  display: true,
                  required: false,
                  default: '',
                  validations: [],
                },
                {
                  section: 'creditInsuranceForm',
                  id: 'B78EEEA8-A838-41DF-B4DD-13D81CF53DB0',
                  name: 'loanTerm',
                  label: 'Loan Term',
                  display: true,
                  required: true,
                  default: '',
                  validations: [],
                },
                {
                  section: 'creditInsuranceForm',
                  id: '73E2BFAD-8E8F-4198-A015-7A13E236D0AC',
                  name: 'loanAmount',
                  label: 'Loan Amount',
                  display: true,
                  required: true,
                  default: '',
                  validations: [],
                },
                {
                  section: 'creditInsuranceForm',
                  id: '73E2BFAD-8E8F-4198-A015-7A13E236D0AC',
                  name: 'monthlyPayment',
                  label: 'Monthly Payment',
                  display: true,
                  required: true,
                  default: '',
                  validations: [],
                },
                {
                  section: 'creditInsuranceForm',
                  id: '73E2BFAD-8E8F-4198-A015-7A13E236D0AC',
                  name: 'loanEndDate',
                  label: 'Loan End Date',
                  display: true,
                  required: false,
                  default: '',
                  validations: [],
                },
                {
                  section: 'creditInsuranceForm',
                  id: 'B78EEEA8-A838-41DF-B4DD-13D81CF53DB0',
                  name: 'downPayment',
                  label: 'Down Payment',
                  display: true,
                  required: false,
                  default: '',
                  validations: [],
                },
                {
                  section: 'creditInsuranceForm',
                  id: '73E2BFAD-8E8F-4198-A015-7A13E236D0AC',
                  name: 'firstPaymentDate',
                  label: 'First Payment Date',
                  display: true,
                  required: false,
                  default: '',
                  validations: [],
                },
                {
                  section: 'creditInsuranceForm',
                  id: '73E2BFAD-8E8F-4198-A015-7A13E236D0AC',
                  name: 'interestRate',
                  label: 'Interest Rate',
                  display: true,
                  required: false,
                  default: '',
                  validations: [],
                },
                {
                  section: 'creditInsuranceForm',
                  id: '73E2BFAD-8E8F-4198-A015-7A13E236D0AC',
                  name: 'amortizationPeriod',
                  label: 'Amortization Period',
                  display: true,
                  required: false,
                  default: '',
                  validations: [],
                },
                {
                  section: 'creditInsuranceForm',
                  id: 'B78EEEA8-A838-41DF-B4DD-13D81CF53DB0',
                  name: 'loanType',
                  label: 'Loan Type',
                  display: true,
                  required: true,
                  default: 'C',
                  validations: [],
                },
                {
                  section: 'creditInsuranceForm',
                  id: '73E2BFAD-8E8F-4198-A015-7A13E236D0AC',
                  name: 'residualValue',
                  label: 'Residual Value',
                  display: true,
                  required: false,
                  default: '',
                  validations: [],
                },
                {
                  section: 'creditInsuranceForm',
                  id: '73E2BFAD-8E8F-4198-A015-7A13E236D0AC',
                  name: 'coMonthlyPayment',
                  label: 'CoBorrower Monthly Payment',
                  display: true,
                  required: false,
                  default: '',
                  validations: [],
                },
                {
                  section: 'creditInsuranceForm',
                  id: '73E2BFAD-8E8F-4198-A015-7A13E236D0AC',
                  name: 'primaryMonthlyPayment',
                  label: 'Primary Monthly Payment',
                  display: true,
                  required: false,
                  default: '',
                  validations: [],
                },
              ];

              return {
                type: CartActions.GET_CHECKOUT_FORM_SUCCESS,
                payload: response,
              };
            }),
            // If request fails, dispatch failed action
            catchError((error) =>
              of({
                type: CartActions.CART_FAILURE,
                payload: error,
              })
            )
          );
        }
      )
    )
  );

  LogOut$: Observable<any> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CartActions.GET_CHECKOUT_FORM_SUCCESS),
        tap(() => this.router.navigate(['/home/shopping/checkout']))
      ),
    { dispatch: false }
  );

  addTaxes$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(CartActions.ADD_TAXES),
      withLatestFrom(
        this.store.select((state) => state.cartState.validatedCart),
        this.store.select((state) => state.userState.user),
        this.store.select((state) => state.productState.request)
      ),
      switchMap(
        ([, validatedCart, user, productRequest]: [
          CartActions.AddTaxesAction,
          CartItem[],
          UserResponse,
          GetProductsRequest
        ]) => {
          const request = new CartTax(user, validatedCart, productRequest);

          return this.apiService.taxCalculation(request).pipe(
            // If successful, dispatch success action with result
            map((response) => {
              return {
                type: CartActions.ADD_TAXES_SUCCESS,
                payload: response.cart,
              };
            }),
            // If request fails, dispatch failed action
            catchError((error) =>
              of({
                type: CartActions.CART_FAILURE,
                payload: error,
              })
            )
          );
        }
      )
    )
  );

  submitCart$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(CartActions.SUBMIT_CART),
      withLatestFrom(
        this.store.select((state) => state.userState.user),
        this.store.select((state) => state.vehicleState.values)
      ),
      switchMap(([action, user, productRequest]: [CartActions.SubmitCartAction, UserResponse, VehicleValues]) => {
        action.payload.dealer = user.cmsDealerNumber;
        action.payload.dealerInfo = user.dealerInfo;
        action.payload.request = {
          ...productRequest,
          customerTaxExempt: productRequest.customerTaxExempt === 'YES',
          make: productRequest.make.toLowerCase() === 'other' ? productRequest.otherMake : productRequest.make,
          model:
            productRequest.other && productRequest.other.toLowerCase() !== 'other'
              ? productRequest.other
              : productRequest.model,
          vin: productRequest.vin || productRequest.hin || productRequest.pid || productRequest.tin,
        };
        action.payload.programId = user.programId;
        action.payload.applicationName = user.applicationName;
        action.payload.userName = user.userName;

        return this.apiService.submitCart(action.payload).pipe(
          // If successful, dispatch success action with result
          withLatestFrom(this.store.select((state) => state.remitState.pendingContracts)),
          map(([response, pendingContracts]: [SubmitCartResponse, ContractType[]]) => {
            const isNotValidCart = response.invalidCart.some((product) => product.valid === false);
            this.store.dispatch(new UpdatePendingNumberAction(pendingContracts.length + response.submittedCart.length));

            if (!isNotValidCart) {
              this.store.dispatch(new ProductActions.ResetProductsAction());
              this.store.dispatch(new VehicleActions.ResetProductFormAction());
              this.store.dispatch(new CartActions.ResetCartAction());
            }

            return {
              type: CartActions.SUBMIT_CART_SUCCESS,
              payload: {
                invalidCart: response.invalidCart,
                submittedCart: response.submittedCart,
              },
            };
          }),
          // If request fails, dispatch failed action
          catchError((error) => {
            return of({
              type: CartActions.CART_FAILURE,
              payload: error,
            });
          })
        );
      })
    )
  );

  saveQuote$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(CartActions.SAVE_AS_QUOTE),
      withLatestFrom(this.store.select((state) => state)),
      switchMap(([, state]: [CartActions.SaveAsQuoteAction, fromRoot.State]) => {
        const quote = new SaveQuote(
          state.productState.request,
          {
            ...state.vehicleState,
            values: {
              ...state.vehicleState.values,
              vin:
                state.vehicleState.values['vin'] ||
                state.vehicleState.values['pid'] ||
                state.vehicleState.values['hin'] ||
                state.vehicleState.values['tin'],
            },
          },
          state.cartState,
          state.userState
        );

        if (state.userState.user.isRelatedDealer) {
          quote['productState'] = state.productState;
        }
        quote.cartState.checkoutFormValues.customer['postalCode'] = quote.cartState.checkoutFormValues.customer[
          'postalCode'
        ]
          ? this.formService.getFormattedPostalCode(quote.cartState.checkoutFormValues.customer['postalCode'])
          : null;
        quote.cartState.checkoutFormValues.cosigner['postalCode'] = quote.cartState.checkoutFormValues.cosigner[
          'postalCode'
        ]
          ? this.formService.getFormattedPostalCode(quote.cartState.checkoutFormValues.cosigner['postalCode'])
          : null;

        if (
          Object.keys(quote.cartState.checkoutFormValues.lienholder).length > 0 &&
          quote.cartState.checkoutFormValues.lienholder['lienholder'] !== null &&
          quote.cartState.checkoutFormValues.lienholder['lienholder'] !== undefined
        ) {
          quote.cartState.checkoutFormValues.lienholder['lienholder']['postalCode'] = quote.cartState.checkoutFormValues
            .lienholder['lienholder']
            ? this.formService.getFormattedPostalCode(
                quote.cartState.checkoutFormValues.lienholder['lienholder']['postalCode']
              )
            : null;
        }

        return this.apiService.saveQuote(quote).pipe(
          // If successful, dispatch success action with result
          map(() => {
            return {
              type: CartActions.SAVE_AS_QUOTE_SUCCESS,
              payload: {},
            };
          }),
          // If request fails, dispatch failed action
          catchError(() =>
            of({
              type: CartActions.SAVE_AS_QUOTE_FAILURE,
              payload: {},
            })
          )
        );
      })
    )
  );

  saveUMUQuote$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(CartActions.SAVE_AS_UMU_QUOTE),
      withLatestFrom(this.store.select((state) => state)),
      switchMap(([request]: [CartActions.SaveAsUMUQuoteAction, fromRoot.State]) => {
        return this.apiService.getrequestQuote(request['payload']).pipe(
          // If successful, dispatch success action with result
          map((response) => {
            if (response.standardResponse?.responseCode === '500') {
              return {
                type: CartActions.SAVE_AS_UMU_QUOTE_FAILURE,
                payload: {
                  errorCode: response.standardResponse.responseCode,
                  errorMessage: response.standardResponse.responseDescription,
                },
              };
            }

            if (response.insResponse.generalInfo.covErrors && response.insResponse.generalInfo.covErrors.length > 0) {
              return {
                type: CartActions.SAVE_AS_UMU_QUOTE_FAILURE,
                payload: {
                  errorCode: response.insResponse.generalInfo.covErrors[0].covCode,
                  errorMessage: response.insResponse.generalInfo.covErrors[0].covError,
                },
              };
            }

            if (!response.insResponse.insurance?.coverage) {
              return {
                type: CartActions.SAVE_AS_UMU_QUOTE_FAILURE,
                payload: {
                  errorCode: response.insResponse.generalInfo.responseStatus,
                  errorMessage: response.insResponse.generalInfo.applicationResponse,
                },
              };
            }

            return {
              type: CartActions.SAVE_AS_UMU_QUOTE_SUCCESS,
              payload: response,
            };
          }),
          // If request fails, dispatch failed action
          catchError((errorResponse) =>
            of({
              type: CartActions.SAVE_AS_UMU_QUOTE_FAILURE,
              payload: {
                errorCode: errorResponse.status,
                errorMessage: errorResponse.error?.insResponse?.generalInfo?.applicationResponse || 'An error occurred',
              },
            })
          )
        );
      })
    )
  );

  saveUMUCredit$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(CartActions.SAVE_AS_UMU_CREDIT),
      withLatestFrom(this.store.select((state) => state)),
      switchMap(([request]: [CartActions.SaveAsUMUCreditAction, fromRoot.State]) => {
        return this.apiService.getUMUCredit(request['payload']).pipe(
          // If successful, dispatch success action with result
          map((response) => {
            if (response.standardResponse?.responseCode === '500') {
              return {
                type: CartActions.SAVE_AS_UMU_CREDIT_FAILURE,
                payload: {
                  errorCode: response.standardResponse.responseCode,
                  errorMessage: response.standardResponse.responseDescription,
                },
              };
            }

            if (response?.insResponse?.responseStatus === '1') {
              return {
                type: CartActions.SAVE_AS_UMU_CREDIT_FAILURE,
                payload: {
                  errorCode: response.insResponse.responseStatus,
                  errorMessage: response.insResponse.applicationResponse,
                },
              };
            }

            const pdf = response.insResponse?.applicationResponse || '';

            function convertToByteArray(input) {
              const sliceSize = 512;
              const bytes = [];

              for (let offset = 0; offset < input.length; offset += sliceSize) {
                const slice = input.slice(offset, offset + sliceSize);

                const byteNumbers = new Array(slice.length);

                for (let i = 0; i < slice.length; i++) {
                  byteNumbers[i] = slice.charCodeAt(i);
                }

                const byteArray = new Uint8Array(byteNumbers);

                bytes.push(byteArray);
              }

              return bytes;
            }

            const blob = atob(pdf);
            const file = new Blob(convertToByteArray(blob), { type: 'application/pdf' });
            const fileURL = URL.createObjectURL(file);
            window.open(fileURL, '_blank');

            return {
              type: CartActions.SAVE_AS_UMU_CREDIT_SUCCESS,
              payload: { response },
            };
          }),
          // If request fails, dispatch failed action
          catchError(() =>
            of({
              type: CartActions.SAVE_AS_UMU_CREDIT_FAILURE,
              payload: {},
            })
          )
        );
      })
    )
  );

  loadQuote$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(CartActions.LOAD_QUOTE),
      withLatestFrom(this.store.select((state) => state)),
      switchMap(([, state]: [CartActions.LoadQuoteAction, fromRoot.State]) => {
        return this.callLoadQuote(state).pipe(
          // If successful, dispatch success action with result
          map((response: LoadQuote) => {
            return {
              type: CartActions.LOAD_QUOTE_SUCCESS,
              payload: response,
            };
          }),
          // If request fails, dispatch failed action
          catchError(() =>
            of({
              type: CartActions.CART_FAILURE,
              payload: {},
            })
          )
        );
      })
    )
  );

  getCustomerPostal$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(CartActions.GET_CUSTOMER_POSTAL),
      switchMap((action: CartActions.GetCustomerPostalAction) => {
        const req = {
          postalcode: action.payload,
        };

        return this.apiService.getPostal(req).pipe(
          // If successful, dispatch success action with result
          map((response: any) => {
            return {
              type: CartActions.GET_CUSTOMER_POSTAL_SUCCESS,
              payload: response,
            };
          }),
          // If request fails, dispatch failed action
          catchError(() =>
            of({
              type: CartActions.CART_FAILURE,
              payload: {},
            })
          )
        );
      })
    )
  );

  getCosignerPostal$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(CartActions.GET_COSIGNER_POSTAL),
      switchMap((action: CartActions.GetCosignerPostalAction) => {
        const req = { postalcode: action.payload };

        return this.apiService.getPostal(req).pipe(
          // If successful, dispatch success action with result
          map((response: any) => {
            return {
              type: CartActions.GET_COSIGNER_POSTAL_SUCCESS,
              payload: response,
            };
          }),
          // If request fails, dispatch failed action
          catchError(() =>
            of({
              type: CartActions.CART_FAILURE,
              payload: {},
            })
          )
        );
      })
    )
  );

  LoadQuoteSuccess$: Observable<any> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CartActions.LOAD_QUOTE_SUCCESS),
        withLatestFrom(this.store.select((state) => state.userState.markup)),
        tap(([action, markup]: [CartActions.LoadQuoteSuccessAction, Markup]) => {
          const productQuote = this.mapProductsFromLoadQuote(action.payload, markup);

          // change cartFound value to false as this quote was loaded and not decoded manually
          action.payload.VehicleState.vin.cartFound = false;

          this.store.dispatch(new ProductActions.ResetSurchargesSelectionAction());
          this.store.dispatch(new VehicleActions.LoadVehicleQuoteAction(action.payload));
          this.store.dispatch(new ProductActions.LoadProductQuoteAction(productQuote));

          // navigate to product page
          this.router.navigate(['/home/shopping/products']);
        })
      ),
    { dispatch: false }
  );

  callLoadQuote(state: fromRoot.State) {
    const dealer = state.userState.user.cmsDealerNumber;
    const vin = state.vehicleState.vin.vin;

    const request = {
      dealer: dealer,
      vin: vin,
    };

    const user = state.userState.user;
    if (user && user.isRelatedDealer) {
      return this.apiService.loadQuoteOnly(request);
    }

    return this.apiService.loadQuote(request);
  }

  // this is mapper for loadquote api call
  mapProductsFromLoadQuote(payload: any, markup: any) {
    const productState = payload.productState as ProductReducer.State;

    // if there is a product state, then use that (this happens for PRIME flow)
    if (productState) {
      return {
        request: productState.request,
        response: productState.products,
        gridProducts: productState.grid,
        filters: productState.filters,
        markup: productState.markup,
      };
    }

    // else recreate the product state from available options and standard response
    const getProductsResponse = new GetProductsResponse();
    getProductsResponse.availableOptions = payload.availableOptions;
    getProductsResponse.standardResponse = payload.StandardResponse;

    const rawProducts =
      payload.availableOptions.products.productItem.filter(
        (product) => !MutedProductCodes.includes(product.productCode)
      ) ?? [];

    const filters = this.filterService.fetchFilters(rawProducts);
    const gridProducts = ProductService.productResponseDataMapper(rawProducts);

    return {
      request: payload.getProductRequest,
      response: getProductsResponse,
      gridProducts: gridProducts,
      filters: filters,
      markup: markup,
    };
  }
}
